const RELOAD_GUARD_KEY = 'vite_preload_error_reloaded_at';

// A stale chunk is fixed by one reload; a genuinely broken one never is. The
// cooldown tells them apart without having to know which it was: a second
// failure seconds after a reload means the reload did not help, so stop.
// Minutes later — a new deploy, a recovered connection — the next failure is
// a different event and earns its own reload.
//
// Deliberately not keyed by __APP_VERSION__: `develop` deploys on every merge
// while package.json's version is bumped by hand, so many builds with
// different asset hashes ship under a single version string. Keying by version
// would spend a tab's only reload on the first deploy and strand it on the next.
const RELOAD_COOLDOWN_MS = 30_000;

/**
 * Listens for Vite's `vite:preloadError` — a lazy chunk/CSS 404 because this
 * tab is running an older build whose asset hashes no longer exist on the
 * server. One reload picks up the new build.
 *
 * Returns a disposer, matching `subscribeAuthSession`'s subscribe/unsubscribe
 * shape, so a caller can stop listening.
 */
export function watchPreloadErrors(): () => void {
	const onPreloadError = (): void => {
		const lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY));
		const withinCooldown =
			Number.isFinite(lastReloadAt) && Date.now() - lastReloadAt < RELOAD_COOLDOWN_MS;

		if (withinCooldown) return;

		sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
		window.location.reload();
	};

	window.addEventListener('vite:preloadError', onPreloadError);

	return () => window.removeEventListener('vite:preloadError', onPreloadError);
}
